import type { RecognizedLabelData, ScanCaptureDetails, ScanOcrDetails, ScanRecognitionTiming } from "../types/scanWorkflow";
import { finalizeStep2Uncertainty, type Step2UncertaintyFinalization } from "./step2UncertaintyUxService";
import {
  productionExecutionProfileEvidence,
  recognitionParallelismEvidence,
  warmRecognitionParallelismProbe,
} from "./recognitionParallelismProbeService";

export type VisionFeedbackField = "materialNumber" | "materialText" | "materialType" | "storageAddress";
export type VisionFeedbackSignalClass = "user_corrected" | "user_confirmed_unchanged" | "master_resolved" | "final_only" | "missing";
export type VisionFeedbackIntervention = "manual" | "master_dialog" | "suggestion";

export interface VisionFeedbackBaseline {
  capturedAt: string;
  labelData: RecognizedLabelData;
  ocrDetails: ScanOcrDetails;
  analysisInputSha256: string | null;
  runtimeApi: string | null;
  bundleId: string | null;
  bundleVersion: string | null;
  recognitionTiming?: ScanRecognitionTiming | null;
  step2Uncertainty?: Step2UncertaintyFinalization | null;
}

export interface VisionRetakeSignal {
  at: string;
  capturedAt: string | null;
  elapsedMs: number | null;
  reason: "user_retake_step2";
  quality: ScanCaptureDetails["quality"];
  mappingConfidence: number | null;
  detectionFound: boolean | null;
  finalGeometryAccepted: boolean | null;
  detectionScore: number | null;
  detectionMethod: string | null;
  ocrStatus: ScanOcrDetails["status"] | null;
  ocrFieldCount: number;
  recognizedMaterialNumber: string;
  geometryGroundTruth: false;
}

export interface VisionFeedbackBuildInput {
  commitment: "draft" | "completed";
  baseline: VisionFeedbackBaseline | null;
  finalLabelData: RecognizedLabelData;
  interventions: Partial<Record<VisionFeedbackField, VisionFeedbackIntervention>>;
  step2ConfirmedAt: string | null;
  retakes: VisionRetakeSignal[];
  scanDetails: ScanCaptureDetails | null;
  ocrWarpAvailable: boolean;
  clientUuid?: string | null;
  withdrawalNumber?: number | null;
  serverId?: number | null;
}

const FIELDS: VisionFeedbackField[] = ["materialNumber", "storageAddress", "materialType", "materialText"];

function clean(value: unknown): string {
  return String(value ?? "").replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim();
}

function normalized(field: VisionFeedbackField, value: unknown): string {
  const text = clean(value);
  if (field === "materialNumber") return text.replace(/\D/g, "");
  if (field === "storageAddress") return text.toLocaleUpperCase("de-DE");
  if (field === "materialType") return text.replace(/\s*-\s*/g, "-");
  return text.toLocaleUpperCase("de-DE");
}

function ocrField(details: ScanOcrDetails | null | undefined, field: VisionFeedbackField) {
  return details?.fields.find((item) => item.field === field) ?? null;
}

function masterResolution(details: ScanOcrDetails | null | undefined, field: VisionFeedbackField) {
  return details?.masterResolution?.fields.find((item) => item.field === field) ?? null;
}

function weightFor(signalClass: VisionFeedbackSignalClass): number {
  if (signalClass === "user_corrected") return 1;
  if (signalClass === "master_resolved") return 0.85;
  if (signalClass === "user_confirmed_unchanged") return 0.65;
  if (signalClass === "final_only") return 0.35;
  return 0;
}

export function createVisionFeedbackBaseline(
  labelData: RecognizedLabelData,
  ocrDetails: ScanOcrDetails,
  scanDetails: ScanCaptureDetails | null,
): VisionFeedbackBaseline {
  // SR37 finalizes the four Step-2 values and their uncertainty state
  // synchronously before this baseline returns. ScanWorkflow calls this before
  // advanceCaptureToStep2(), so navigation can never race the uncertainty UX.
  const step2Uncertainty = finalizeStep2Uncertainty(labelData, ocrDetails, scanDetails);
  // PV-1: Probe startet erst NACH fachlich fertiger Recognition und blockiert Step 2 nie.
  void warmRecognitionParallelismProbe().catch(() => undefined);
  return {
    capturedAt: scanDetails?.capturedAt ?? new Date().toISOString(),
    labelData: { ...labelData },
    ocrDetails: structuredClone(ocrDetails),
    analysisInputSha256: scanDetails?.detection?.analysisInputSha256 ?? null,
    runtimeApi: scanDetails?.detection?.visionSnapshot?.runtimeApi ?? null,
    bundleId: scanDetails?.detection?.visionSnapshot?.bundleId ?? null,
    bundleVersion: scanDetails?.detection?.visionSnapshot?.bundleVersion ?? null,
    recognitionTiming: scanDetails?.recognitionTiming ?? null,
    step2Uncertainty,
  };
}

export function createRetakeSignal(
  scanDetails: ScanCaptureDetails | null,
  ocrDetails: ScanOcrDetails,
  labelData: RecognizedLabelData,
): VisionRetakeSignal {
  const capturedAtMs = scanDetails?.capturedAt ? Date.parse(scanDetails.capturedAt) : Number.NaN;
  return {
    at: new Date().toISOString(),
    capturedAt: scanDetails?.capturedAt ?? null,
    elapsedMs: Number.isFinite(capturedAtMs) ? Math.max(0, Date.now() - capturedAtMs) : null,
    reason: "user_retake_step2",
    quality: scanDetails?.quality ?? null,
    mappingConfidence: scanDetails?.mapping?.confidence ?? null,
    detectionFound: scanDetails?.detection?.found ?? null,
    finalGeometryAccepted: scanDetails?.detection?.finalGeometryAccepted ?? null,
    detectionScore: scanDetails?.detection?.score ?? null,
    detectionMethod: scanDetails?.detection?.detectionMethod ?? null,
    ocrStatus: ocrDetails?.status ?? null,
    ocrFieldCount: ocrDetails?.fields.filter((field) => clean(field.text)).length ?? 0,
    recognizedMaterialNumber: clean(labelData.materialNumber),
    geometryGroundTruth: false,
  };
}

export function buildVisionFeedbackPayload(input: VisionFeedbackBuildInput): Record<string, unknown> {
  const baseline = input.baseline;
  const recognitionTiming = input.scanDetails?.recognitionTiming ?? baseline?.recognitionTiming ?? null;
  let correctedFields = 0;
  let confirmedFields = 0;
  let masterResolvedFields = 0;
  let eligibleFields = 0;

  const fields = Object.fromEntries(FIELDS.map((field) => {
    const finalValue = clean(input.finalLabelData[field]);
    const step1Value = clean(baseline?.labelData[field]);
    const ocr = ocrField(baseline?.ocrDetails, field);
    const master = masterResolution(baseline?.ocrDetails, field);
    const finalNormalized = normalized(field, finalValue);
    const step1Normalized = normalized(field, step1Value);
    const ocrValue = clean(ocr?.text);
    const ocrRaw = clean(ocr?.rawText);
    const intervention = input.interventions[field] ?? null;
    const uncertaintyAtStep2 = baseline?.step2Uncertainty?.fields?.[field] ?? null;

    let signalClass: VisionFeedbackSignalClass;
    if (!finalValue) {
      signalClass = "missing";
    } else if (!baseline) {
      signalClass = "final_only";
    } else if (finalNormalized !== step1Normalized) {
      signalClass = "user_corrected";
      correctedFields += 1;
    } else if (
      master?.source === "master"
      && normalized(field, master.resolvedValue) === finalNormalized
      && normalized(field, master.ocrValue) !== finalNormalized
    ) {
      signalClass = "master_resolved";
      masterResolvedFields += 1;
    } else {
      signalClass = "user_confirmed_unchanged";
      confirmedFields += 1;
    }

    const eligible = Boolean(
      input.commitment === "completed"
      && input.ocrWarpAvailable
      && baseline
      && finalValue
      && signalClass !== "missing"
      && signalClass !== "final_only"
    );
    if (eligible) eligibleFields += 1;

    return [field, {
      field,
      ocr_raw: ocrRaw,
      ocr_value: ocrValue,
      ocr_confidence: ocr?.confidence ?? null,
      selected_pass: ocr?.selectedPass ?? null,
      pass_count: ocr?.passCount ?? 1,
      storage_address_decoder: field === "storageAddress" ? (ocr?.storageAddressDecoder ?? null) : null,
      step1_value: step1Value,
      final_value: finalValue,
      signal_class: signalClass,
      weight: weightFor(signalClass),
      eligible_for_ocr_learning: eligible,
      intervention,
      uncertainty_at_step2: uncertaintyAtStep2,
      uncertainty_cleared_by_actual_correction: Boolean(uncertaintyAtStep2?.status === "uncertain" && finalNormalized !== step1Normalized),
      master_resolution: master ? {
        ocr_value: clean(master.ocrValue),
        resolved_value: clean(master.resolvedValue),
        source: master.source,
        reason: master.reason,
        confidence: master.confidence,
        similarity: master.similarity,
      } : null,
    }];
  }));

  return {
    schema_version: 1,
    feedback_version: "vision-feedback-v1",
    created_at: new Date().toISOString(),
    commitment: input.commitment,
    identity: {
      client_uuid: input.clientUuid ?? null,
      withdrawal_number: input.withdrawalNumber ?? null,
      server_id: input.serverId ?? null,
    },
    scope: "ocr-field-feedback",
    recognition_run_id: recognitionTiming?.recognitionRunId ?? null,
    recognition_timing: recognitionTiming ? {
      schema_version: recognitionTiming.schemaVersion,
      contract_version: recognitionTiming.contractVersion,
      recognition_run_id: recognitionTiming.recognitionRunId,
      shutter_at_utc: recognitionTiming.shutterAtUtc,
      step2_data_ready_at_utc: recognitionTiming.step2DataReadyAtUtc,
      shutter_to_step2_data_ready_ms: recognitionTiming.shutterToStep2DataReadyMs,
      duration_clock: recognitionTiming.durationClock,
      runtime_temperature: recognitionTiming.runtimeTemperature,
      runtime_temperature_basis: recognitionTiming.runtimeTemperatureBasis,
      runtime_telemetry: recognitionTiming.runtimeTelemetry ? {
        schema_version: recognitionTiming.runtimeTelemetry.schemaVersion,
        contract_version: recognitionTiming.runtimeTelemetry.contractVersion,
        captured_at_utc: recognitionTiming.runtimeTelemetry.capturedAtUtc,
        device: {
          instance_id: recognitionTiming.runtimeTelemetry.device.instanceId,
          label: recognitionTiming.runtimeTelemetry.device.label,
          device_class: recognitionTiming.runtimeTelemetry.device.deviceClass,
          platform: recognitionTiming.runtimeTelemetry.device.platform,
          logical_cpu_count: recognitionTiming.runtimeTelemetry.device.logicalCpuCount,
          device_memory_gb: recognitionTiming.runtimeTelemetry.device.deviceMemoryGb,
          installed_pwa: recognitionTiming.runtimeTelemetry.device.installedPwa,
          screen_width: recognitionTiming.runtimeTelemetry.device.screenWidth,
          screen_height: recognitionTiming.runtimeTelemetry.device.screenHeight,
          device_pixel_ratio: recognitionTiming.runtimeTelemetry.device.devicePixelRatio,
        },
        browser: {
          family: recognitionTiming.runtimeTelemetry.browser.family,
          version: recognitionTiming.runtimeTelemetry.browser.version,
          major_version: recognitionTiming.runtimeTelemetry.browser.majorVersion,
          engine: recognitionTiming.runtimeTelemetry.browser.engine,
          user_agent: recognitionTiming.runtimeTelemetry.browser.userAgent,
        },
        runtime: {
          runtime_api: recognitionTiming.runtimeTelemetry.runtime.runtimeApi,
          production_pipeline_sha256: recognitionTiming.runtimeTelemetry.runtime.productionPipelineSha256,
          bundle_id: recognitionTiming.runtimeTelemetry.runtime.bundleId,
          bundle_version: recognitionTiming.runtimeTelemetry.runtime.bundleVersion,
          recognition_revision: recognitionTiming.runtimeTelemetry.runtime.recognitionRevision,
          detection_backend: recognitionTiming.runtimeTelemetry.runtime.detectionBackend,
          detection_worker_state: recognitionTiming.runtimeTelemetry.runtime.detectionWorkerState,
          ocr_backend: recognitionTiming.runtimeTelemetry.runtime.ocrBackend,
          ocr_preprocess_runtime: recognitionTiming.runtimeTelemetry.runtime.ocrPreprocessRuntime,
          ocr_worker_state: recognitionTiming.runtimeTelemetry.runtime.ocrWorkerState,
          cnn_enabled: recognitionTiming.runtimeTelemetry.runtime.cnnEnabled,
          cnn_runtime: recognitionTiming.runtimeTelemetry.runtime.cnnRuntime,
          webgpu_available: recognitionTiming.runtimeTelemetry.runtime.webGpuAvailable,
          cross_origin_isolated: recognitionTiming.runtimeTelemetry.runtime.crossOriginIsolated,
          shared_array_buffer_available: recognitionTiming.runtimeTelemetry.runtime.sharedArrayBufferAvailable,
          wasm_available: recognitionTiming.runtimeTelemetry.runtime.wasmAvailable,
          execution_profile: productionExecutionProfileEvidence(input.scanDetails),
          parallelism_probe: recognitionParallelismEvidence(),
        },
      } : null,
    } : null,
    step2_uncertainty_finalization: baseline?.step2Uncertainty ?? null,
    step2_uncertainty_finalized_before_navigation: baseline?.step2Uncertainty?.finalizationBeforeNavigation === true,
    geometry_ground_truth: false,
    eligible_for_geometry_learning: false,
    eligible_for_ocr_learning: input.commitment === "completed" && eligibleFields > 0,
    step2_confirmed_at: input.step2ConfirmedAt,
    baseline: baseline ? {
      captured_at: baseline.capturedAt,
      analysis_input_sha256: baseline.analysisInputSha256,
      runtime_api: baseline.runtimeApi,
      bundle_id: baseline.bundleId,
      bundle_version: baseline.bundleVersion,
      ocr_runtime: baseline.ocrDetails.engine,
      ocr_status: baseline.ocrDetails.status,
      grid_anchoring: baseline.ocrDetails.gridAnchoring ?? null,
      parallel_shadow: baseline.ocrDetails.parallelShadow ?? null,
      parallel_production: baseline.ocrDetails.parallelProduction ?? null,
    } : null,
    fields,
    summary: {
      eligible_fields: eligibleFields,
      user_corrected_fields: correctedFields,
      user_confirmed_fields: confirmedFields,
      master_resolved_fields: masterResolvedFields,
      retake_count: input.retakes.length,
      retake_hard_case_signal: input.retakes.length > 0,
    },
    retakes: input.retakes,
    capture_quality: input.scanDetails?.quality ?? null,
    mapping_confidence: input.scanDetails?.mapping?.confidence ?? null,
    geometry_contract: input.scanDetails?.geometryContract ?? null,
    rectification_runtime: input.scanDetails?.rectification ? {
      used: input.scanDetails.rectification.used,
      input_rgba_sha256: input.scanDetails.rectification.inputRgbaSha256 ?? null,
      output_rgba_sha256: input.scanDetails.rectification.outputRgbaSha256 ?? null,
      timing: input.scanDetails.rectification.timing ?? null,
      parallel_shadow: input.scanDetails.rectification.parallelShadow ?? null,
    } : null,
    detection: input.scanDetails?.detection ? {
      found: input.scanDetails.detection.found,
      final_geometry_accepted: input.scanDetails.detection.finalGeometryAccepted,
      score: input.scanDetails.detection.score,
      method: input.scanDetails.detection.detectionMethod,
      processing_time_ms: input.scanDetails.detection.processingTimeMs,
      top_k_rerank: input.scanDetails.detection.topKRerank ?? null,
      corner_refinement: input.scanDetails.detection.cornerRefinement ?? null,
    } : null,
  };
}

export function buildVisionFeedbackBlob(input: VisionFeedbackBuildInput): Blob {
  return new Blob([JSON.stringify(buildVisionFeedbackPayload(input), null, 2)], { type: "application/json" });
}

export async function readVisionFeedbackBaseline(blob: Blob | null | undefined): Promise<{
  baseline: VisionFeedbackBaseline | null;
  retakes: VisionRetakeSignal[];
} | null> {
  if (!blob) return null;
  try {
    const parsed = JSON.parse(await blob.text()) as Record<string, unknown>;
    const draftState = parsed.draft_state as Record<string, unknown> | undefined;
    const rawBaseline = draftState?.baseline as VisionFeedbackBaseline | undefined;
    const rawRetakes = draftState?.retakes as VisionRetakeSignal[] | undefined;
    if (!rawBaseline) return null;
    return {
      baseline: rawBaseline,
      retakes: Array.isArray(rawRetakes) ? rawRetakes : [],
    };
  } catch {
    return null;
  }
}

export function buildDraftVisionFeedbackBlob(
  baseline: VisionFeedbackBaseline | null,
  retakes: VisionRetakeSignal[],
): Blob | null {
  if (!baseline && retakes.length === 0) return null;
  return new Blob([JSON.stringify({
    schema_version: 1,
    feedback_version: "vision-feedback-v1",
    commitment: "draft",
    scope: "workflow-feedback-checkpoint",
    geometry_ground_truth: false,
    eligible_for_geometry_learning: false,
    eligible_for_ocr_learning: false,
    draft_state: { baseline, retakes },
    created_at: new Date().toISOString(),
  }, null, 2)], { type: "application/json" });
}
